import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/base/PageSEO';
import { categories, hotHeadlines, latestArticles, featuredArticle } from '@/mocks/articles';

const allArticles = [featuredArticle, ...hotHeadlines, ...latestArticles];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = allArticles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 51黑料网`,
    description: `51黑料网${category.name}分类，共${category.count}篇爆料内容`,
    url: `http://3kyzh.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51黑料网',
      url: 'http://3kyzh.com/',
    },
    about: {
      '@type': 'Thing',
      name: category.name,
      description: `51黑料网${category.name}分类聚合`,
    },
  };

  return (
    <>
      <PageSEO
        title={`${category.name} - 51黑料网`}
        description={`51黑料网${category.name}分类，共${category.count}篇爆料内容，每日实时更新全网最新热门${category.name}相关黑料爆料与吃瓜事件，第一时间掌握真相。`}
        keywords={`51黑料网,${category.name},黑料爆料,吃瓜事件,热门汇总`}
        canonical={`http://3kyzh.com/category/${category.slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="text-xs text-foreground-500 mb-6 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{category.name}</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-4 mb-3">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500">共 {category.count} 篇爆料内容</p>
              </div>
            </div>
          </header>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-background-300/80 transition-all duration-300"
                >
                  <div className="relative h-48 md:h-52 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-4">{article.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article.views} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 mb-4">该分类下暂无内容</p>
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line"></i>返回首页
              </Link>
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
